import { zodResolver } from "@hookform/resolvers/zod";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, DropdownSelect, ErrorMessage, Icon, Loading, LocationInput, Panel, TextInput, Typography } from "@/components/atoms";
import { GeneralLogo } from "@/components/organisms/General/GeneralLogo";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { getRegistrationTypeOptions, getRegistrationTypeSpec, validateRegistrationId } from "@/helpers/registrationValidators";
import { useCountries, useCountryConfig, useOperator } from "@/hooks";
import { useLazyGetProviderQuery, useRegisterProviderWithKindeMutation } from "@/redux/apis/auth/authApi";
import { useGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useGetKycCountryQuery } from "@/redux/apis/kyc/kycApi";
import { useConnectStripeMutation } from "@/redux/apis/payment/stripe/stripeApi";
import { useAppDispatch } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { addToast } from "@/utils/addToast";
import { getCountryName } from "@/utils";

/**
 * Validation keys off the registration *type* the operator picked, not their country — a UTR is a
 * UTR whoever asks. That keeps this a module-level const (so `FormData` stays a plain `z.infer`)
 * and means the backend can accept a new type without a frontend release, since an unrecognised
 * type validates as-is and defers to the server.
 */
const formSchema = z
  .object({
    country: z.string().default("AU"),
    registrationType: z.string().min(1, "Field is required and can't be empty"),
    businessName: z.string().min(1, "Field is required and can't be empty"),
    registrationId: z.string().min(1, "Field is required and can't be empty"),
    businessAddress: z.string().min(1, "Field is required and can't be empty"),
  })
  .superRefine((data, ctx) => {
    const { valid, message } = validateRegistrationId(data.registrationType, data.registrationId);

    if (!valid) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["registrationId"] });
    }
  });

type FormData = z.infer<typeof formSchema>;

interface PlatformSetupStepProps {
  onSetupComplete: () => void;
}

export const PlatformSetupStep = ({ onSetupComplete }: PlatformSetupStepProps) => {
  const { user } = useKindeAuth();
  const dispatch = useAppDispatch();
  const { bookableCountryList, country: operatorCountry } = useOperator();
  /**
   * Onboarding runs KYC before this step, and Sumsub already established the operator's country —
   * web-app-v2 writes it to account metadata on approval. Prefer it over the tenant's default so
   * the operator is not asked to state a country they have just proved. Absent for accounts
   * onboarded before this was captured, or when Sumsub reported a country we cannot map, in which
   * case this falls back to the previous behaviour exactly.
   */
  const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery();
  const profileKycCountry = profile?.accountMetadata?.kycCountry;
  /**
   * Sumsub approval is asynchronous, so the applicantReviewed webhook that writes kyc_country may
   * not have landed by the time this screen renders — which is how a UK operator ended up being
   * asked for an ABN. Rather than poll for a value a rejected review would never produce, ask
   * Sumsub directly, and only when the profile has not already answered. The endpoint writes what
   * it finds back to account metadata, so the race closes for every later reader too.
   */
  const { data: resolvedKyc } = useGetKycCountryQuery(undefined, {
    skip: isProfileLoading || Boolean(profileKycCountry),
  });
  const kycCountry = profileKycCountry || resolvedKyc?.country || undefined;
  const defaultCountry = kycCountry || operatorCountry;
  const [registerProvider, { isLoading: isSubmitting }] = useRegisterProviderWithKindeMutation();
  const [connectStripe] = useConnectStripeMutation();
  const [getProvider] = useLazyGetProviderQuery();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      country: defaultCountry,
      registrationType: "",
      businessName: "",
      registrationId: "",
      businessAddress: "",
    },
  });

  const country = watch("country");
  const { config } = useCountryConfig(country);
  const { bookable } = useCountries(bookableCountryList);
  /**
   * Only ask when we do not already know. A tenant serving several countries still needs the
   * picker for an operator whose KYC country we could not resolve, but showing it to someone whose
   * country Sumsub just verified is asking the same question twice.
   */
  const showCountrySelector = bookableCountryList.length > 1 && !kycCountry;

  const registrationTypes = config.businessRegistrationTypes;
  const registrationType = watch("registrationType") || registrationTypes[0] || "";
  const registrationSpec = getRegistrationTypeSpec(registrationType);
  /** GB accepts CRN, UTR or VAT, so it needs a picker; AU/US/NZ have a single implied type. */
  const showRegistrationTypeSelector = registrationTypes.length > 1;

  /**
   * `defaultValues` is read once, on first render, and the profile query has not resolved by then
   * — so the KYC country has to be written in when it arrives or the form would keep the tenant
   * default it started with. Only while the field is untouched: if the selector was shown because
   * KYC gave us nothing, a late-arriving value must not overwrite what the operator picked.
   */
  useEffect(() => {
    if (kycCountry && !showCountrySelector) {
      setValue("country", kycCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycCountry, showCountrySelector]);

  // Keep the form's type in step with the country the operator has selected.
  useEffect(() => {
    if (registrationTypes.length && !registrationTypes.includes(watch("registrationType"))) {
      setValue("registrationType", registrationTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, registrationTypes.join(",")]);

  const getCountryOptions = () =>
    bookable.map(({ countryCode, name }) => ({ name: name || getCountryName(countryCode) || countryCode, value: countryCode }));

  /**
   * Create the operator's own Stripe Connect account, as soon as there is a provider to hang it on.
   *
   * Nothing else in onboarding does this, so without it a provider completes signup with no
   * `secret-stripe-account-id` and every payment path throws — the operator is the merchant of
   * record (charges carry `on_behalf_of`), so there is no account to fall back to. It is a
   * prerequisite for transacting at all, not a setting, which is why it belongs here rather than
   * only behind Settings > Payments.
   *
   * Same endpoint the Stripe configuration panel calls, deliberately: it creates the account only
   * when none is stored and otherwise reconciles status against the live account, so calling it
   * here and again later is safe.
   *
   * Deliberately does not block signup. If Stripe is unreachable the operator should still finish
   * onboarding and pick a plan; the configuration panel remains the recovery path, and it will
   * create the account on first visit exactly as it does today. Failing signup over this would
   * turn a Stripe outage into an inability to sign up at all.
   */
  const createStripeConnectAccount = async (providerId: string, countryCode: string) => {
    const settingsUrl = `${getPhpHostUrl()}/dashboard/payments/payment-configuration`;

    try {
      await connectStripe({ refresh_url: `${settingsUrl}?refresh=1`, redirect_url: settingsUrl }).unwrap();
    } catch (error) {
      // Not surfaced to the operator: they have no context for it mid-signup, and it is recoverable
      // without them knowing. The payment configuration panel is where it becomes visible and
      // actionable.
      //
      // It IS reported, though: Sentry's captureConsoleIntegration forwards console.error outside
      // development. The provider and country are named because without them the event says only
      // that *someone* failed, and six providers reached the payments page with no Connect account
      // before anyone could tell which step had dropped (DEV-1693).
      console.error(`Failed to create the Stripe Connect account during onboarding [${providerId} ${countryCode}]:`, error);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!user) {
      addToast("danger", "User not authenticated");
      return;
    }

    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    try {
      const requestBody = {
        email: user.email || "",
        first_name: user.givenName || "",
        last_name: user.familyName || "",
        business_registration_type: registrationType,
        business_registration_id: data.registrationId,
        business_name: data.businessName,
        business_address: data.businessAddress,
        country_code: data.country,
      };

      const result = await registerProvider(requestBody).unwrap();

      const providerId = result.provider_uuid;

      if (providerId) {
        let isActiveOperatorSet = false;

        try {
          const operator = await getProvider(providerId).unwrap();
          dispatch(setActiveOperator(operator));
          isActiveOperatorSet = true;
        } catch (error) {
          // Names the provider for the same reason as the Connect failure below: this is the other
          // branch that can silently skip account creation, and the two were indistinguishable in
          // Sentry while both logged a bare sentence. The request itself answered 200 for every
          // signup investigated, so a failure here is the transform, not the network.
          console.error(`Failed to fetch operator data [${providerId} ${data.country}]:`, error);
        }

        // Gated on the operator actually being in state: the payment API builds its URL from
        // `activeOperator.id`, so firing this after a failed fetch would POST to
        // /v3/provider/undefined/payment/... rather than creating anything.
        if (isActiveOperatorSet) {
          await createStripeConnectAccount(providerId, data.country);
        }
      }

      addToast("success", "Platform setup complete");
      onSetupComplete();
    } catch (error) {
      console.error("Registration error:", error);
      getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
      setIsProcessing(false);
    }
  });

  return (
    <Panel className="relative h-fit w-[440px] p-8">
      {(isSubmitting || isProcessing) && <Loading className="rounded-lg" />}

      {/* Logo and Brand */}
      <div className="mb-8">
        <GeneralLogo />
      </div>

      {/* Title and Description */}
      <div className="mb-6">
        <Typography variant="h1" className="mb-2">
          Let&apos;s get you set up
        </Typography>
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          We just need a few basics to get you up and running.
        </Typography>
      </div>

      {/* Form */}
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        {/* Country Selector */}
        {showCountrySelector && (
          <div className="flex flex-col gap-1">
            <Typography variant="action" className="text-neutral-dark-gray">
              Country
            </Typography>
            <DropdownSelect
              className="w-full"
              placeholder="Select Country"
              options={getCountryOptions()}
              value={country}
              onChange={(value: string) => {
                setValue("country", value);
                setValue("registrationType", "");
                setValue("registrationId", "");
              }}
            />
          </div>
        )}

        {/* Registered Business Name */}
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Registered Business Name
          </Typography>
          <TextInput placeholder="Demo Co." maxLength={45} hasError={!!formErrors.businessName} {...register("businessName")} />
          <ErrorMessage errors={formErrors} name="businessName" />
        </div>

        {/* Business registration type — only where the country accepts more than one */}
        {showRegistrationTypeSelector && (
          <div className="flex flex-col gap-1">
            <Typography variant="action" className="text-neutral-dark-gray">
              Business Registration Type
            </Typography>
            <DropdownSelect
              className="w-full"
              placeholder="Select Type"
              options={getRegistrationTypeOptions(registrationTypes)}
              value={registrationType}
              onChange={(value: string) => {
                setValue("registrationType", value);
                setValue("registrationId", "");
              }}
            />
          </div>
        )}

        {/* Business registration identifier (ABN/EIN/CRN…) */}
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            {registrationSpec?.label ?? "Business Registration ID"}
          </Typography>
          {/**
           * Type and length come from the identifier spec. This was `type="number"` with
           * maxLength 16 for AU, which made a GB company number (SC123456) or a VAT number
           * (GB123456789) literally impossible to type.
           */}
          <TextInput
            type={registrationSpec?.inputType ?? "text"}
            placeholder={registrationSpec?.placeholder ?? ""}
            maxLength={20}
            hasError={!!formErrors.registrationId}
            {...register("registrationId")}
          />
          <ErrorMessage errors={formErrors} name="registrationId" />
        </div>

        {/* Registered Business Address */}
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Registered Business Address
          </Typography>
          <Controller
            name="businessAddress"
            control={control}
            render={({ field }) => (
              <LocationInput
                placeholder="Search for your business address"
                country={country}
                renderIcon={() => <Icon name="Location" size="md" variant="Bold" className="text-primary" />}
                onLocationChange={(value) => {
                  field.onChange(value?.address || "");
                }}
                hasError={!!formErrors.businessAddress}
                allowManualInput={true}
                defaultValue={field.value}
              />
            )}
          />
          <ErrorMessage errors={formErrors} name="businessAddress" />
        </div>

        {/* Save Button */}
        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting || isProcessing}>
          Save
        </Button>
      </form>
    </Panel>
  );
};
